package com.tovtalk.data.network

import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock

/**
 * Dedupes concurrent calls that share the same key. The first call executes
 * the block; every other caller that arrives while the first is still running
 * awaits the same result.
 *
 * Usage:
 * ```
 * val result = dedup.run("feed:discover:${cursor}") { fetchFromNetwork() }
 * ```
 *
 * Results are not cached — once the block returns, its entry is removed and
 * the next call for the same key starts fresh. This is the right behavior for
 * network requests where we want to collapse thundering-herd but not serve
 * stale data beyond the in-flight window.
 */
class InFlightRequestDeduplicator<K : Any> {

    private val mutex = Mutex()
    private val inFlight = mutableMapOf<K, CompletableDeferred<Any?>>()

    @Suppress("UNCHECKED_CAST")
    suspend fun <T> run(key: K, block: suspend () -> T): T {
        val (deferred, isLeader) = mutex.withLock {
            val existing = inFlight[key]
            if (existing != null) {
                existing to false
            } else {
                val fresh = CompletableDeferred<Any?>()
                inFlight[key] = fresh
                fresh to true
            }
        }

        if (!isLeader) {
            return deferred.await() as T
        }

        try {
            val result = block()
            deferred.complete(result)
            return result
        } catch (t: Throwable) {
            deferred.completeExceptionally(t)
            throw t
        } finally {
            mutex.withLock { inFlight.remove(key) }
        }
    }
}
